import React, { useState } from 'react';
import {
  MdArrowBack, MdNotificationsActive, MdFingerprint, MdLanguage,
  MdPalette, MdAccessibility, MdLockOutline, MdEmail, MdSms, MdPhoneIphone,
  MdChevronRight, MdSmartToy, MdBolt,
} from 'react-icons/md';
import { useApp } from '../contexts/AppContext';

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    style={{
      // A clear rounded-rectangle (pill) track — the off-state fill/border use a
      // semi-opaque neutral so the switch shape reads on BOTH the white light-theme
      // card and the dark purple-theme card. (The old --bh-border var was undefined,
      // so the off track had no border and its near-white fill vanished, leaving
      // just the white knob — which looked like a bare circle, not a toggle.)
      // -webkit-appearance:none is REQUIRED on mobile Safari — without it iOS
      // renders its own native button chrome over these inline styles, so the
      // custom pill track disappears and only the round knob shows (it looks like
      // a bare circle). Desktop Chromium ignores this, which is why it only
      // reproduced on the actual phone.
      WebkitAppearance: 'none', appearance: 'none', margin: 0, outline: 'none',
      width: 48, height: 28, padding: 2, flexShrink: 0, boxSizing: 'border-box',
      background: checked ? 'var(--td-green)' : 'rgba(120,120,128,0.32)',
      border: '1px solid ' + (checked ? 'var(--td-green)' : 'rgba(120,120,128,0.45)'),
      borderRadius: 999,
      cursor: 'pointer', position: 'relative',
      display: 'inline-flex', alignItems: 'center',
      transition: 'background 0.15s ease, border-color 0.15s ease',
    }}
  >
    <span
      style={{
        display: 'block',
        width: 22, height: 22,
        background: '#ffffff',
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
        transform: checked ? 'translateX(20px)' : 'translateX(0)',
        transition: 'transform 0.15s ease',
      }}
    />
  </button>
);

// A plain div, NOT a <button>: these rows can contain their own interactive
// controls (the Toggle switch is itself a <button>), and a button-inside-a-
// button is invalid HTML whose inner control won't receive clicks — which is
// why the notification/accessibility toggles were unresponsive.
const Row = ({ Icon, tint, title, sub, right, onClick, last }) => (
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
      width: '100%', padding: 'var(--space-sm)', boxSizing: 'border-box',
      background: 'transparent', border: 'none', textAlign: 'left',
      borderBottom: last ? 'none' : '1px solid var(--border-color)',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <span style={{
      width: 36, height: 36, background: tint, color: 'var(--bh-white)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, borderRadius: 'var(--radius-md)',
    }}>
      <Icon size={18} />
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ font: 'var(--bh-body)', fontWeight: 600, color: 'var(--text-dark)' }}>{title}</div>
      {sub && <div style={{ font: 'var(--bh-caption)', color: 'var(--text-medium)', marginTop: 2 }}>{sub}</div>}
    </div>
    {right}
  </div>
);

const BACKEND_OPTIONS = [
  { value: 'cohere',      label: 'Cohere',      tier: 'Free'  },
  { value: 'openrouter',  label: 'OpenRouter',  tier: 'Free'  },
  { value: 'gemini',      label: 'Gemini',      tier: 'Free'  },
  { value: 'groq',        label: 'Groq',        tier: 'Free'  },
  { value: 'claude',      label: 'Claude',      tier: 'Paid'  },
  { value: 'openai',      label: 'OpenAI',      tier: 'Paid'  },
  { value: 'ollama',      label: 'Ollama',      tier: 'Local' },
  { value: 'omlx',        label: 'oMLX',        tier: 'Local' },
];

const SettingsScreen = ({ onNavigate, onBack }) => {
  const { selectedBackend, setSelectedBackend, ollamaNoThink, setOllamaNoThink } = useApp() || {};

  const [pushOn, setPushOn]   = useState(true);
  const [emailOn, setEmailOn] = useState(true);
  const [smsOn, setSmsOn]     = useState(false);
  const [bioOn, setBioOn]     = useState(true);
  const [highContrast, setHighContrast] = useState(false);
  const [largeText, setLargeText]       = useState(false);
  const [theme, setTheme]   = useState('green');
  const [language, setLang] = useState('English');

  return (
    <div className="screen bh-flow settings-screen">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('profileScreen'))} aria-label="Back to profile">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Settings</h1>
        </div>
      </header>

      <div className="bh-flow-body">
        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Notifications</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            <Row Icon={MdNotificationsActive} tint="var(--td-amber)"
                 title="Push notifications" sub="Alerts on this device"
                 right={<Toggle checked={pushOn} onChange={setPushOn} label="Push notifications" />} />
            <Row Icon={MdEmail} tint="var(--td-blue)"
                 title="Email" sub="justinl@tdlab.io"
                 right={<Toggle checked={emailOn} onChange={setEmailOn} label="Email notifications" />} />
            <Row Icon={MdSms} tint="var(--td-purple)"
                 title="Text message" sub="•••••• 4421"
                 right={<Toggle checked={smsOn} onChange={setSmsOn} label="SMS notifications" />} last />
          </div>
        </section>

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Security</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            <Row Icon={MdFingerprint} tint="var(--td-premium)"
                 title="Biometric sign-in" sub="Face ID on this device"
                 right={<Toggle checked={bioOn} onChange={setBioOn} label="Biometric sign-in" />} />
            <Row Icon={MdLockOutline} tint="var(--td-red)"
                 title="Change password" sub="Last changed 3 months ago"
                 right={<MdChevronRight color="var(--text-light)" />}
                 onClick={() => onNavigate('security')} />
            <Row Icon={MdPhoneIphone} tint="var(--bh-black)"
                 title="Trusted devices" sub="2 devices registered"
                 right={<MdChevronRight color="var(--text-light)" />}
                 onClick={() => onNavigate('security')} last />
          </div>
        </section>

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Display</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            <Row Icon={MdPalette} tint="var(--td-gold)"
                 title="Theme" sub={theme === 'green' ? 'Premium green' : 'High-contrast'}
                 right={<MdChevronRight color="var(--text-light)" />}
                 onClick={() => setTheme(theme === 'green' ? 'highContrast' : 'green')} />
            <Row Icon={MdLanguage} tint="var(--td-teal)"
                 title="Language" sub={language}
                 right={<MdChevronRight color="var(--text-light)" />}
                 onClick={() => setLang(language === 'English' ? 'Français' : 'English')} last />
          </div>
        </section>

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Accessibility</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            <Row Icon={MdAccessibility} tint="var(--td-lavender)"
                 title="High contrast" sub="Stronger borders and text"
                 right={<Toggle checked={highContrast} onChange={setHighContrast} label="High contrast" />} />
            <Row Icon={MdAccessibility} tint="var(--td-brown)"
                 title="Larger text" sub="Increase base font size"
                 right={<Toggle checked={largeText} onChange={setLargeText} label="Larger text" />} last />
          </div>
        </section>

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>AI Assistant</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            <Row Icon={MdSmartToy} tint="var(--td-teal)"
                 title="Backend"
                 sub={BACKEND_OPTIONS.find(o => o.value === selectedBackend)?.label ?? 'Cohere'}
                 right={
                   <select
                     value={selectedBackend ?? 'cohere'}
                     onChange={e => setSelectedBackend?.(e.target.value)}
                     style={{ fontSize: 13, border: 'none', background: 'transparent', color: 'var(--text-medium)', cursor: 'pointer' }}
                   >
                     <optgroup label="Free">
                       <option value="cohere">Cohere</option>
                       <option value="openrouter">OpenRouter</option>
                       <option value="gemini">Gemini</option>
                       <option value="groq">Groq</option>
                     </optgroup>
                     <optgroup label="Paid">
                       <option value="claude">Claude</option>
                       <option value="openai">OpenAI</option>
                     </optgroup>
                     <optgroup label="Local">
                       <option value="ollama">Ollama</option>
                       <option value="omlx">oMLX</option>
                     </optgroup>
                   </select>
                 }
            />
            {selectedBackend === 'ollama' && (
              <Row Icon={MdBolt} tint={ollamaNoThink ? 'var(--td-amber)' : 'var(--td-purple)'}
                   title={ollamaNoThink ? 'Fast mode (No Think)' : 'Think mode'}
                   sub={ollamaNoThink ? 'Skips chain-of-thought for faster replies' : 'Reasons before replying — slower, more accurate'}
                   right={<Toggle checked={!!ollamaNoThink} onChange={v => setOllamaNoThink?.(v)} label="Ollama fast mode" />}
                   last />
            )}
          </div>
        </section>

        <div className="bh-info-box">
          Settings sync across devices when you sign in. Changes save automatically.
        </div>
      </div>
    </div>
  );
};

export default SettingsScreen;
